import { useState, useRef, useCallback } from 'react';
import { 
  Upload, 
  FileText, 
  CheckCircle2, 
  AlertCircle, 
  X, 
  RefreshCw, 
  Database, 
  Cpu, 
  Hash, 
  Tag, 
  Layers, 
  ShieldCheck, 
  FileCheck,
  Search,
  ArrowUpRight
} from 'lucide-react';
import { Button } from '../../../components/ui/Button.jsx';
import { Badge } from '../../../components/ui/Badge.jsx';
import { Input } from '../../../components/ui/Input.jsx';
import { cn } from '../../../lib/utils.js';

export const ALLOWED_EXTENSIONS = ['.pdf', '.doc', '.docx', '.txt'];
export const ALLOWED_MIME_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'text/plain',
];
export const MAX_FILE_SIZE_BYTES = 50 * 1024 * 1024; // 50 MiB = 52,428,800 bytes

export const DOCUMENT_CATEGORIES = [
  'Datenblatt',
  'Norm / Richtlinie',
  'Montageanleitung',
  'Zertifikat & Zulassung',
  'Technisches Handbuch',
  'Schaltplan / Schema',
];

export const NORM_REFERENCES = [
  'DIN 14675 (Brandmeldeanlagen)',
  'DIN EN 54 (BMA Produktnormen)',
  'VdS 2311 (Einbruchmeldeanlagen)',
  'DIN EN 50131 (EMA Produktnormen)',
  'DIN EN 62676 (Videoüberwachung / VSS)',
  'DIN VDE 0833 (Gefahrenmeldeanlagen)',
  'Allgemein / Keine Norm',
];

export const INITIAL_UPLOAD_HISTORY = [
  {
    id: 'upl_init_001',
    name: 'Datenblatt SATEL ACSP-402 Brandmeldezentrale.pdf',
    fileName: 'SATEL_ACSP-402_de.pdf',
    size: 2451920,
    formattedSize: '2.34 MB',
    category: 'Datenblatt',
    norm: 'DIN 14675 (Brandmeldeanlagen)',
    sku: 'SAT-ACSP-402',
    tags: ['SATEL', 'ACSP', 'BMA Zentrale'],
    hash: 'e3b0c44298fc1c149afbf4c8996fb92427ae41e4649b934ca495991b7852b855',
    uploadedAt: '2026-08-21T07:45:00Z',
    formattedDate: '21.08.2026, 09:45 Uhr (Demo)',
    isDemo: true,
  },
  {
    id: 'upl_init_002',
    name: 'VdS 2311 Richtlinien für Einbruchmeldeanlagen.pdf',
    fileName: 'VdS_2311_Planung_Einbau.pdf',
    size: 8912400,
    formattedSize: '8.50 MB',
    category: 'Norm / Richtlinie',
    norm: 'VdS 2311 (Einbruchmeldeanlagen)',
    sku: null,
    tags: ['VdS', 'EMA', 'Richtlinie'],
    hash: '7d5a99f603f231d539eeecd85ff49a54c91f58fae2e5e07a0d16e00a97ecb823',
    uploadedAt: '2026-08-20T16:20:00Z',
    formattedDate: '20.08.2026, 18:20 Uhr (Demo)',
    isDemo: true,
  },
];

export function formatBytes(bytes, decimals = 2) {
  if (!bytes || bytes === 0) return '0 Bytes';
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
}

export function formatGermanTimestamp(date = new Date()) {
  const d = new Date(date);
  const pad = (n) => String(n).padStart(2, '0');
  const day = pad(d.getDate());
  const month = pad(d.getMonth() + 1);
  const year = d.getFullYear();
  const hours = pad(d.getHours());
  const minutes = pad(d.getMinutes());
  return `${day}.${month}.${year}, ${hours}:${minutes} Uhr`;
}

export async function computeFileSha256(file) {
  try {
    if (typeof crypto !== 'undefined' && crypto.subtle && file && file.arrayBuffer) {
      const buffer = await file.arrayBuffer();
      const hashBuffer = await crypto.subtle.digest('SHA-256', buffer);
      const hashArray = Array.from(new Uint8Array(hashBuffer));
      return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
    }
  } catch (e) {
    // Fallback for SSR or non-crypto environments
  }
  const str = (file?.name || 'file') + (file?.size || 0);
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = (hash << 5) - hash + str.charCodeAt(i);
    hash |= 0;
  }
  const hex = Math.abs(hash).toString(16).padStart(16, '0');
  return `sha256_${hex}fe912a7c40b8`;
}

export function validateUploadFile(file, maxSizeBytes = MAX_FILE_SIZE_BYTES, allowedExts = ALLOWED_EXTENSIONS) {
  if (!file) {
    return { isValid: false, error: 'Keine Datei ausgewählt.' };
  }

  // 1. Check empty file
  if (file.size === 0) {
    return { isValid: false, error: 'Die ausgewählte Datei ist leer (0 Bytes).' };
  }

  // 2. Check file size
  if (file.size > maxSizeBytes) {
    const maxMb = Math.round(maxSizeBytes / (1024 * 1024));
    return {
      isValid: false,
      error: `Die Datei überschreitet die maximale Dateigröße von ${maxMb} MiB (Dateigröße: ${formatBytes(file.size)}).`,
    };
  }

  // 3. Check extension
  const fileName = file.name || '';
  const ext = fileName.substring(fileName.lastIndexOf('.')).toLowerCase();
  
  if (!ext || !allowedExts.includes(ext)) {
    if (ext === '.exe' || ext === '.bat' || ext === '.cmd' || ext === '.sh') {
      return {
        isValid: false,
        error: `Dateityp ${ext} wird aus Sicherheitsgründen nicht unterstützt. Nur Dokumentformate (PDF, DOC, DOCX, TXT) sind zulässig.`,
      };
    }
    return {
      isValid: false,
      error: `Ungültiges Dateiformat "${ext}". Es werden ausschließlich ${allowedExts.join(', ').toUpperCase()} Dokumente unterstützt.`,
    };
  }

  return { isValid: true, error: null };
}

export function RagUploadCenter({
  onUploadComplete,
  maxSizeBytes = MAX_FILE_SIZE_BYTES,
  allowedExtensions = ALLOWED_EXTENSIONS,
  className,
}) {
  const [dragActive, setDragActive] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [validationError, setValidationError] = useState(null);
  
  // Metadata state
  const [docTitle, setDocTitle] = useState('');
  const [category, setCategory] = useState(DOCUMENT_CATEGORIES[0]);
  const [norm, setNorm] = useState(NORM_REFERENCES[0]);
  const [tags, setTags] = useState('');
  const [sku, setSku] = useState('');

  // Upload lifecycle state: 'idle' | 'selected' | 'uploading' | 'success'
  const [uploadState, setUploadState] = useState('idle');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadedResult, setUploadedResult] = useState(null);

  // Upload History
  const [history, setHistory] = useState(INITIAL_UPLOAD_HISTORY);
  const [historySearch, setHistorySearch] = useState('');

  const fileInputRef = useRef(null);

  const resetForm = () => {
    setSelectedFile(null);
    setValidationError(null);
    setDocTitle('');
    setCategory(DOCUMENT_CATEGORIES[0]);
    setNorm(NORM_REFERENCES[0]);
    setTags('');
    setSku('');
    setUploadState('idle');
    setUploadProgress(0);
    setUploadedResult(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleFileSelect = (file) => {
    setValidationError(null);
    const validation = validateUploadFile(file, maxSizeBytes, allowedExtensions);
    
    if (!validation.isValid) {
      setValidationError(validation.error);
      setSelectedFile(null);
      setUploadState('idle');
      return;
    }

    setSelectedFile(file);
    const baseName = file.name.replace(/\.[^/.]+$/, '');
    setDocTitle(baseName);
    setUploadState('selected');
  };

  // Drag & Drop handlers
  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      setDragActive(false);
      if (e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files.length > 0) {
        handleFileSelect(e.dataTransfer.files[0]);
      }
    },
    [maxSizeBytes, allowedExtensions]
  );

  const handleFileInputChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFileSelect(e.target.files[0]);
    }
  };

  const handleTriggerFilePicker = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleStartUpload = async (e) => {
    if (e) e.preventDefault();
    if (!selectedFile) return;

    // Fail-closed: No fake timer simulations without real persisting backend
    setValidationError('Upload deaktiviert: Es ist keine persistierende Ingestion-Pipeline angebunden.');
    setUploadState('idle');
  };

  const filteredHistory = history.filter((item) => {
    if (!historySearch) return true;
    const term = historySearch.toLowerCase();
    return (
      item.name.toLowerCase().includes(term) ||
      item.fileName.toLowerCase().includes(term) ||
      item.category.toLowerCase().includes(term) ||
      item.norm.toLowerCase().includes(term) ||
      (item.sku && item.sku.toLowerCase().includes(term))
    );
  });

  return (
    <div
      data-testid="rag-upload-center"
      className={cn('space-y-8 rounded-2xl border border-[var(--border,#e2e8f0)] bg-white p-6 sm:p-8 shadow-xs', className)}
    >
      {/* Ingestion Pipeline Deactivation Notice */}
      <div
        data-testid="rag-upload-deactivated-notice"
        className="flex items-center gap-3 rounded-xl border border-amber-200 bg-amber-50/80 p-4 text-xs text-amber-900"
      >
        <AlertCircle className="h-5 w-5 text-amber-600 shrink-0" />
        <div className="flex-1 space-y-0.5">
          <strong className="font-bold">Upload-Pipeline deaktiviert (Vorschau-Modus): </strong>
          <span>
            Kein persistierendes Ingestion-Backend angebunden. Dokument-Uploads und automatische Vectorize-Indexierung erfordern ein aktives Server-Binding.
          </span>
        </div>
        <Badge variant="outline" className="bg-amber-100 text-amber-800 border-amber-300 text-[10px] font-bold shrink-0">
          Deaktiviert
        </Badge>
      </div>

      {/* Upload Center Header */}
      <div className="flex flex-wrap items-center justify-between gap-4 pb-6 border-b border-[var(--border,#e2e8f0)]">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-red-50 text-[var(--primary,#e3000b)]">
              <Upload className="h-4 w-4" />
            </div>
            <h2 className="font-display text-lg sm:text-xl font-bold text-[var(--foreground,#181716)]">
              RAG Ingestion &amp; Upload Center
            </h2>
          </div>
          <p className="text-xs sm:text-sm text-[var(--secondary,#475569)]">
            Laden Sie neue technische Datenblätter, Normen und Montageanleitungen hoch. Die Dokumente werden automatisch tokenisiert, in Cloudflare Vectorize (BGE-M3) eingebettet und in D1 verknüpft.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <Badge variant="outline" className="bg-slate-100 text-slate-700 border-slate-300 text-xs font-semibold py-1 px-3">
            BGE-M3 Auto-Vectorization
          </Badge>
          <Badge variant="outline" className="bg-slate-100 text-slate-700 border-slate-300 text-xs font-mono font-medium py-1 px-2.5">
            Max 50 MiB
          </Badge>
        </div>
      </div>

      {/* Upload Zone & Interactive States */}
      {uploadState === 'idle' && (
        <div className="space-y-4">
          <div
            data-testid="rag-drop-zone"
            aria-disabled="true"
            className={cn(
              'relative flex flex-col items-center justify-center rounded-2xl border-2 border-dashed p-8 sm:p-12 text-center transition-all cursor-not-allowed select-none opacity-80 border-slate-300 bg-slate-50/60'
            )}
          >
            <input
              ref={fileInputRef}
              data-testid="rag-file-input"
              type="file"
              disabled
              accept=".pdf,.doc,.docx,.txt,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain"
              className="hidden"
            />

            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-white shadow-sm border border-slate-200 text-slate-400 mb-4">
              <Upload className="h-8 w-8 text-slate-400" />
            </div>

            <h3 className="font-display text-base font-bold text-slate-900">
              Dokument per Drag &amp; Drop hier ablegen
            </h3>
            <p className="mt-1 text-xs text-slate-500 max-w-md">
              Upload derzeit deaktiviert. Unterstützte Formate nach Backend-Anbindung: <strong className="text-slate-700">PDF, DOC, DOCX und TXT</strong> bis maximal 50 MiB.
            </p>

            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled
              className="mt-5 text-xs font-bold border-slate-300 text-slate-500 bg-white shadow-2xs pointer-events-none cursor-not-allowed"
            >
              <FileText className="h-3.5 w-3.5 mr-1.5 text-slate-400" />
              <span>Upload deaktiviert (Kein Ingestion-Backend)</span>
            </Button>
          </div>

          {/* Validation Error Banner */}
          {validationError && (
            <div
              data-testid="rag-upload-error"
              className="flex items-center gap-3 rounded-xl border border-red-200 bg-red-50 p-4 text-xs text-red-900 animate-fadeIn"
            >
              <AlertCircle className="h-5 w-5 text-red-600 shrink-0" />
              <div className="flex-1">
                <strong className="font-bold">Hinweis: </strong>
                <span>{validationError}</span>
              </div>
              <button
                type="button"
                onClick={() => setValidationError(null)}
                className="text-red-500 hover:text-red-700 p-1"
                aria-label="Fehler schließen"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          )}
        </div>
      )}

      {/* Selected File & Metadata Form */}
      {uploadState === 'selected' && selectedFile && (
        <form
          data-testid="rag-metadata-form"
          onSubmit={handleStartUpload}
          className="space-y-6 rounded-2xl border border-slate-200 bg-[var(--surface-container-low,#f8fafc)] p-6 animate-fadeIn"
        >
          {/* Selected file banner */}
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white p-4">
            <div className="flex items-center gap-3 min-w-0">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-red-50 text-[var(--primary,#e3000b)]">
                <FileText className="h-5 w-5" />
              </div>
              <div className="min-w-0">
                <p className="font-bold text-sm text-slate-900 truncate">
                  {selectedFile.name}
                </p>
                <p className="text-xs text-slate-500 font-mono">
                  {formatBytes(selectedFile.size)} • {selectedFile.type || 'Dokument'}
                </p>
              </div>
            </div>

            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={resetForm}
              className="text-xs text-slate-500 hover:text-red-600 gap-1 cursor-pointer"
            >
              <X className="h-3.5 w-3.5" />
              <span>Entfernen</span>
            </Button>
          </div>

          {/* Metadata Inputs */}
          <div className="space-y-4">
            <h3 className="font-display text-sm font-bold text-slate-900 flex items-center gap-2">
              <Tag className="h-4 w-4 text-[var(--primary,#e3000b)]" />
              <span>Metadaten &amp; Normen-Klassifizierung erfassen</span>
            </h3>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Document Title */}
              <div className="space-y-1.5 sm:col-span-2">
                <label className="text-xs font-semibold text-slate-700">
                  Dokumenten-Titel im RAG-Index *
                </label>
                <Input
                  data-testid="rag-meta-title"
                  type="text"
                  required
                  value={docTitle}
                  onChange={(e) => setDocTitle(e.target.value)}
                  placeholder="z. B. Datenblatt SATEL ACSP-402 Brandmeldezentrale"
                  className="bg-white text-xs h-9"
                />
              </div>

              {/* Category Dropdown */}
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-slate-700">
                  Dokumenttyp / Kategorie *
                </label>
                <select
                  data-testid="rag-meta-category"
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-xs text-slate-900 focus:border-[var(--primary,#e3000b)] focus:outline-none"
                >
                  {DOCUMENT_CATEGORIES.map((cat) => (
                    <option key={cat} value={cat}>
                      {cat}
                    </option>
                  ))}
                </select>
              </div>

              {/* Norm Dropdown */}
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-slate-700">
                  Norm-Referenz (ALWIS Regelwerk) *
                </label>
                <select
                  data-testid="rag-meta-norm"
                  value={norm}
                  onChange={(e) => setNorm(e.target.value)}
                  className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-xs text-slate-900 focus:border-[var(--primary,#e3000b)] focus:outline-none"
                >
                  {NORM_REFERENCES.map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>
              </div>

              {/* Tags Input */}
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-slate-700">
                  Schlagworte (Tags, kommagetrennt)
                </label>
                <Input
                  data-testid="rag-meta-tags"
                  type="text"
                  value={tags}
                  onChange={(e) => setTags(e.target.value)}
                  placeholder="z. B. SATEL, BMA, Loop-Koppler, EN54"
                  className="bg-white text-xs h-9"
                />
              </div>

              {/* SKU Association */}
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-slate-700">
                  Zugehörige Artikelnummer / SKU (optional)
                </label>
                <Input
                  data-testid="rag-meta-sku"
                  type="text"
                  value={sku}
                  onChange={(e) => setSku(e.target.value)}
                  placeholder="z. B. SAT-ACSP-402 oder 100429"
                  className="bg-white text-xs h-9 font-mono"
                />
              </div>
            </div>
          </div>

          {/* Submit / Action Bar */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-200">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={resetForm}
              className="text-xs text-slate-600 hover:text-slate-900 cursor-pointer"
            >
              Abbrechen
            </Button>

            <Button
              type="submit"
              variant="default"
              size="sm"
              data-testid="rag-submit-btn"
              className="text-xs font-bold gap-1.5 bg-[var(--primary,#e3000b)] text-white hover:bg-[var(--primary,#e3000b)]/90 cursor-pointer shadow-xs"
            >
              <Upload className="h-3.5 w-3.5" />
              <span>In RAG-Vektorindex einspeisen</span>
            </Button>
          </div>
        </form>
      )}

      {/* Uploading In-Progress State */}
      {uploadState === 'uploading' && (
        <div
          data-testid="rag-upload-progress"
          className="space-y-4 rounded-2xl border border-blue-200 bg-blue-50/50 p-8 text-center animate-fadeIn"
        >
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-xl bg-blue-100 text-blue-700">
            <RefreshCw className="h-6 w-6 animate-spin" />
          </div>

          <div className="max-w-md mx-auto space-y-1">
            <h3 className="font-display text-base font-bold text-slate-900">
              Dokument wird verarbeitet &amp; indexiert...
            </h3>
            <p className="text-xs text-slate-600">
              Extrahiere Text, generiere 1024-dim BGE-M3 Embeddings und synchronisiere Cloudflare Vectorize.
            </p>
          </div>

          {/* Progress bar */}
          <div className="max-w-md mx-auto space-y-1.5">
            <div className="flex justify-between text-xs font-mono font-semibold text-slate-700">
              <span>Ingestion-Fortschritt</span>
              <span>{uploadProgress}%</span>
            </div>
            <div className="h-2.5 w-full overflow-hidden rounded-full bg-slate-200">
              <div
                className="h-full bg-[var(--primary,#e3000b)] transition-all duration-300 rounded-full"
                style={{ width: `${uploadProgress}%` }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Success Banner */}
      {uploadState === 'success' && uploadedResult && (
        <div
          data-testid="rag-upload-success"
          className="space-y-6 rounded-2xl border border-emerald-200 bg-emerald-50/60 p-6 animate-fadeIn"
        >
          <div className="flex flex-wrap items-center justify-between gap-4 pb-4 border-b border-emerald-200">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-100 text-emerald-700">
                <FileCheck className="h-6 w-6" />
              </div>
              <div>
                <h3 className="font-display text-base font-bold text-emerald-950">
                  Dokument erfolgreich indexiert!
                </h3>
                <p className="text-xs text-emerald-800">
                  Das Dokument steht ab sofort für das RAG-Retrieval und den ALWIS Fachberater zur Verfügung.
                </p>
              </div>
            </div>

            <Button
              type="button"
              variant="outline"
              size="sm"
              data-testid="rag-reset-btn"
              onClick={resetForm}
              className="text-xs font-bold bg-white text-emerald-800 border-emerald-300 hover:bg-emerald-50 cursor-pointer"
            >
              <span>Weiteres Dokument hochladen</span>
            </Button>
          </div>

          {/* Structured Confirmation Details */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 text-xs">
            <div className="rounded-xl border border-emerald-200 bg-white p-3 space-y-1">
              <span className="text-[11px] text-slate-500 font-medium">Dokumenten-Name</span>
              <p className="font-bold text-slate-900 truncate">{uploadedResult.name}</p>
              <p className="text-[10px] text-slate-400 font-mono">{uploadedResult.fileName} ({uploadedResult.formattedSize})</p>
            </div>

            <div className="rounded-xl border border-emerald-200 bg-white p-3 space-y-1">
              <span className="text-[11px] text-slate-500 font-medium">Kategorie &amp; Norm</span>
              <p className="font-bold text-slate-900">{uploadedResult.category}</p>
              <p className="text-[10px] text-emerald-700 font-semibold">{uploadedResult.norm}</p>
            </div>

            <div className="rounded-xl border border-emerald-200 bg-white p-3 space-y-1 sm:col-span-2 lg:col-span-1">
              <span className="text-[11px] text-slate-500 font-medium">SHA-256 Prüfsumme</span>
              <p className="font-mono text-[10px] text-slate-700 truncate">{uploadedResult.hash}</p>
              <div className="flex items-center gap-1.5 pt-0.5">
                <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px] font-bold">
                  Vectorize &amp; D1 Synced
                </Badge>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Upload History Table */}
      <div className="space-y-4 pt-4 border-t border-[var(--border,#e2e8f0)]">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h3 className="font-display text-base font-bold text-[var(--foreground,#181716)]">
              Dokumenten-Historie &amp; Upload-Protokoll
            </h3>
            <p className="text-xs text-[var(--secondary,#475569)]">
              {history.length} Dokumente im Verlauf (inkl. Demo-Muster)
            </p>
          </div>

          <div className="relative w-full sm:w-64">
            <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-slate-400" />
            <Input
              type="text"
              placeholder="Historie durchsuchen..."
              value={historySearch}
              onChange={(e) => setHistorySearch(e.target.value)}
              className="h-8.5 pl-8 text-xs bg-[var(--surface-container-low,#f8fafc)]"
            />
          </div>
        </div>

        <div
          data-testid="rag-upload-history"
          className="divide-y divide-slate-100 rounded-xl border border-slate-200 bg-white overflow-hidden text-xs shadow-2xs"
        >
          {filteredHistory.length === 0 ? (
            <div className="p-6 text-center text-xs text-slate-500">
              Keine passenden Dokumente in der Historie gefunden.
            </div>
          ) : (
            filteredHistory.map((item) => (
              <div
                key={item.id}
                className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-3.5 hover:bg-slate-50 transition-colors"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-red-50 text-[var(--primary,#e3000b)]">
                    <FileText className="h-4 w-4" />
                  </div>
                  <div className="min-w-0 space-y-0.5">
                    <p className="font-bold text-slate-900 truncate">
                      {item.name}
                    </p>
                    <div className="flex flex-wrap items-center gap-2 text-[11px] text-slate-500">
                      <span>{item.category}</span>
                      <span>•</span>
                      <span className="text-slate-700 font-medium">{item.norm}</span>
                      {item.sku && (
                        <>
                          <span>•</span>
                          <span className="font-mono text-slate-600">SKU: {item.sku}</span>
                        </>
                      )}
                      <span>•</span>
                      <span className="font-mono">{item.formattedSize}</span>
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-between sm:justify-end gap-3 shrink-0">
                  <span className="font-mono text-[11px] text-slate-500">
                    {item.formattedDate}
                  </span>
                  {item.isDemo ? (
                    <Badge variant="outline" className="bg-amber-50 text-amber-800 border-amber-200 font-semibold text-[10px]" data-testid="rag-demo-history-badge">
                      Muster (Demo)
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 font-semibold text-[10px]">
                      Session-Upload
                    </Badge>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
